import { useEffect, useRef, useState } from "react"
import { Button } from "~/components/ui/button"
import type { Packages, UserPayment, Users } from "@prisma/client"
import { Purchase } from "~/components/purchase"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog"
import { Slider } from "~/components/ui/slider"
import Typography from "~/components/typography"
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar"
import moment from "moment"
import { Textarea } from "~/components/ui/textarea"
import { LoveNoteCurrency } from "~/components/icons"
import { CubeIcon, Cross2Icon } from "@radix-ui/react-icons"
import { Separator } from "~/components/ui/separator"
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group"
import { Label } from "~/components/ui/label"
import { LoveObjectSelection } from "./loveObjects"
import { PurchaseWithPackage } from "~/purchases/purchases.server"

const MAX_CHAR_LIMIT = 280

export function LoveNoteButton({
  className,
  user,
  avatar,
  video,
  cards,
  pkgs,
  loveObjects,
  setPurchase,
  onClick,
}: {
  className?: string
  user: Users
  avatar: string
  video: any
  cards: UserPayment[]
  pkgs: Packages[]
  loveObjects: Packages[]
  setPurchase?: (p: PurchaseWithPackage) => void
  onClick?: () => void
}) {
  const [open, setOpen] = useState(false)
  const [openPurchase, setOpenPurchase] = useState(false)
  const [amount, setAmount] = useState([1])
  const [sortedPkgs, setSortedPkgs] = useState<Packages[]>([])
  const [selectedPkg, setSelectedPkg] = useState<Packages | null>(null)
  const [comment, setComment] = useState("")
  const [isAnonymous, setIsAnonymous] = useState(false)
  const [openLoveObjectSelection, setOpenLoveObjectSelection] = useState(true)
  const [selectedLoveObject, setSelectedLoveObject] = useState<Packages | null>(null)
  const [selectedLoveObjectSrc, setSelectedLoveObjectSrc] = useState("")

  const input = useRef()

  useEffect(() => {
    if (pkgs && pkgs.length > 0) {
      setSortedPkgs(pkgs.sort((a, b) => Number(a.Price) - Number(b.Price)))
    }
  }, [pkgs])

  useEffect(() => {
    if (amount[0] === 0) {
      setAmount([1])
      return
    }

    if (sortedPkgs) setSelectedPkg(sortedPkgs[amount[0] - 1])
  }, [sortedPkgs, amount])

  useEffect(() => {
    input?.current?.focus()
    input?.current?.select()
  }, [])

  useEffect(() => {
    if (!open) {
      setOpenLoveObjectSelection(true)
    }
  }, [open])

  const handleLoveObjectSelection = (loveObject: Packages, src: string) => {
    setSelectedLoveObject(loveObject)
    setSelectedLoveObjectSrc(src)
  }

  return (
    <>
      <Button
        className={className}
        onClick={() => {
          setOpen(true)
          onClick && onClick()
        }}>
        <LoveNoteCurrency className="mr-2 h-5 w-5" />
        Love Note
      </Button>
      {pkgs.length > 0 && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-[526px] max-h-full sm:max-h[70vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-medium px-5 sm:px-0">
                Want to send <b>{video.StarDisplayName}</b> a Love Note?
              </DialogTitle>
              <DialogDescription>
                Show your support and appreciation by sending a Love Note. Your contribution will go directly to{" "}
                <b>{video.StarDisplayName}</b>.
              </DialogDescription>
            </DialogHeader>
            <Separator className="my-2" />
            <div className="flex justify-between items-center">
              <h4 className="text-md tracking-wide font-light">Select an amount:</h4>
              <p className="font-medium text-lg">${selectedPkg?.Price.toString()}</p>
            </div>
            <Slider
              className="cursor-pointer"
              value={amount}
              min={1}
              max={sortedPkgs.length}
              onValueChange={setAmount}
            />
            <Separator />
            <div className="relative bg-background-special border border-primary p-4 rounded-md">
              <div className="absolute h-7 top-0 right-0 transform bg-primary rounded-md">
                <span className="font-medium text-xs text-center text-primary-foreground m-2">Love Note</span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <div className="flex items-center gap-2">
                  <Avatar className={"h-5 w-5"}>
                    <AvatarImage src={avatar} className="object-cover" alt="avatar" />
                    <AvatarFallback>JA</AvatarFallback>
                  </Avatar>
                  <Typography>
                    <h4 className={"font-medium text-[12px] sm:text-[14px] tracking-wide"}>
                      <span>{isAnonymous ? "Secret Admirer" : user.DisplayName}</span>
                    </h4>
                  </Typography>
                  <p className="bg-primary text-primary-foreground flex items-center text-xs gap-[2px] rounded-lg px-[6px] py-[2px]">
                    <LoveNoteCurrency className="h-4 w-4" />
                    {selectedPkg?.Price.toString()}
                  </p>
                  <p className="hidden sm:inline text-muted-foreground text-[9px] sm:text-xs">
                    {moment(new Date()).fromNow()}
                  </p>
                </div>
              </div>
              {selectedLoveObject && (
                <div className="flex justify-center mt-2">
                  <div className="relative border border-[border] rounded-lg">
                    <img
                      src={selectedLoveObjectSrc}
                      alt="Selected Love Object"
                      className="w-24 h-24 sm:w-32 sm:h-32"
                      loading="lazy"
                    />
                    <button
                      className="absolute top-0 right-0 z-20 m-1 border border-[border] rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
                      onClick={() => setSelectedLoveObject(null)}>
                      <Cross2Icon className="h-4 w-4" />
                    </button>
                    <p className="absolute top-0 left-0 bg-primary text-primary-foreground flex items-center text-[11px] gap-[2px] rounded-lg px-[6px] py-[2px]">
                      <LoveNoteCurrency className="h-3 w-3" />
                      {Math.floor(Number(selectedLoveObject.Price))}
                    </p>
                  </div>
                </div>
              )}
              <Typography className="prose-sm mt-2">
                <Textarea
                  className="bg-background-special text-primary placeholder text-center"
                  ref={input}
                  placeholder="Add a note..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  maxLength={MAX_CHAR_LIMIT}
                />
                <p className="text-right text-xs text-muted-foreground">
                  {MAX_CHAR_LIMIT - comment.length} characters remaining
                </p>
              </Typography>
              <div className="flex gap-4 justify-center items-center">
                <RadioGroup
                  name="Private"
                  value={isAnonymous ? "private" : "public"}
                  className="flex gap-4 justify-center items-center mt-2"
                  onValueChange={(value) => setIsAnonymous(value === "private")}>
                  <Label className="flex items-center gap-2 text-xs px-2 cursor-pointer">
                    <RadioGroupItem value="public" />
                    <span>Public</span>
                  </Label>
                  <Label className="flex items-center gap-2 text-xs px-2 cursor-pointer">
                    <RadioGroupItem value="private" />
                    <span>Anonymous</span>
                  </Label>
                </RadioGroup>
                <button
                  className="flex items-center justify-center gap-2 text-foreground mt-2"
                  onClick={() => setOpenLoveObjectSelection(!openLoveObjectSelection)}>
                  <CubeIcon className="h-4 w-4 text-primary" />
                  <span className="font-medium text-xs">Love Objects</span>
                </button>
              </div>
              {openLoveObjectSelection && (
                <LoveObjectSelection
                  open={openLoveObjectSelection}
                  onLoveObjectSelect={handleLoveObjectSelection}
                  selectedLoveObject={selectedLoveObject}
                  onClose={() => setOpenLoveObjectSelection(false)}
                  loveObjects={loveObjects}
                />
              )}
            </div>
            <Separator className="my-3" />
            <DialogFooter>
              <Button
                className="ml-2"
                size="sm"
                onClick={() => {
                  setOpen(false)
                  setOpenPurchase(true)
                }}>
                Buy and Send
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {selectedPkg && (
        <Purchase
          description={
            <>
              You are sending a Love Note to <b>{video.StarDisplayName}</b>.
            </>
          }
          pkg={selectedPkg}
          additionalPkg={selectedLoveObject || undefined}
          cards={cards}
          open={openPurchase}
          setOpen={setOpenPurchase}
          loveNoteVideoID={video.VideoID}
          loveNoteComment={comment}
          isAnonymous={isAnonymous}
          setPurchase={setPurchase}
          starID={video.StarID}
        />
      )}
    </>
  )
}
